import appConfig from '../../config/appConfig';
import gamTimeouts from '../../consts/config/gamTimeouts';
import uamTimeouts from '../../consts/config/uamTimeouts';
import * as gamMethods from '../../consts/gam/gamMethods';
import profilerProcessTypes from '../../consts/log/profilerProcessTypes';
import * as uamMethods from '../../consts/uam/uamMethods';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapConnectionStatusTypes from '../../consts/yap/yapConnectionStatusTypes';
import * as yapFlowTypes from '../../consts/yap/yapFlowTypes';
import DEVICE_TYPES from '../../enums/device/deviceTypes';
import {setIsSkipRegistration, setScpCheckInProgress} from '../../state/ducks/global';
import {
    clearIotDevice,
    clearIotDeviceData,
    clearIotDeviceHolderData,
    setIsDeviceNotSupported,
    setIsHolderDetectedOnInit,
    setIsWrongDevice,
    updateIotDeviceData,
} from '../../state/ducks/iotDevice/actions';
import {
    setDeviceInfoReceived,
    setHolderInfoReceived,
    setOfflineYapFirmwareInProgress,
    setSyncLastRequestId,
    setYapDeviceConnected,
    setYapHolderConnected,
    setYapSyncInProgress,
} from '../../state/ducks/yapEncrypted';
import {
    makeSelectConsumerInfoProduct,
    makeSelectIotDeviceMergedWithConsumerProduct,
} from '../../state/selectors/consumer';
import {
    selectIotDeviceType,
    selectIotHolderType,
    selectIsDeviceNotSupported,
    selectIsHolderConnected,
} from '../../state/selectors/iotDevice';
import {selectMwDeviceInternalId} from '../../state/selectors/mwDevice';
import {
    makeSelectAssetByType,
    makeSelectAssetIdByType,
    makeSelectIsDeviceSyncInProgress,
    makeSelectIsHolderByAssetId,
    makeSelectIsOfflineYapFirmwareInProgress,
    makeSelectIsYapDeviceInfoReceived,
    makeSelectIsYapHolderInfoReceived,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import backendService from '../app/backendService';
import cmClientService from '../communicationLayer/cmClientService';
import cmService from '../communicationLayer/cmService';
import connectivityService from '../device/connectivityService';
import devicePreSyncPingScheduler from '../device/devicePreSyncPingScheduler';
import GamClient from '../gam/gamClient';
import gamClientService from '../gam/gamClientService';
import iotDeviceService from '../iotDevice/iotDeviceService';
import log from '../logger/log';
import profiler from '../logger/profiler';
import marketConfigService from '../marketConfigService';
import pairingService from '../pairingService';
import scpService from '../scp/scpService';
import UamClient from '../uam/uamClient';
import uamClientService from '../uam/uamClientService';
import yapWebSocketService from '../yap/yapWebSocketService';
import yapClientService from './yapClientService';
import yapFlowTimeoutService from './yapFlowTimeoutService';
import yapService from './yapService';
import yapStateService from './yapStateService';

const initScpData = async ({
    isReconnect,
    isHolderOnly,
    initialDeviceType,
    isHolderDetect,
    readHolderSerialNumberTimeoutMs,
}) => {
    const deviceType = selectIotDeviceType(getState());
    const holderType = selectIotHolderType(getState());

    if (isReconnect) {
        dispatch(clearIotDeviceData());
    }

    if (!pairingService.isDeviceConnected()) return {};

    dispatch(setScpCheckInProgress(true));

    const onError = (isHolder) => {
        if (isHolder) {
            dispatch(clearIotDeviceHolderData());
            return {};
        }

        dispatch(clearIotDevice());

        if (isHolder === undefined) {
            dispatch(setScpCheckInProgress(false));
        }
        return {};
    };

    const scpReadResult = await scpService.identifyDevice({isHolderOnly, readHolderSerialNumberTimeoutMs});

    if (!scpReadResult) return onError();

    devicePreSyncPingScheduler.startPing();

    const {holder, charger} = scpReadResult;

    if (!charger) return onError();

    const initDeviceData = async (scpData, isHolder = false) => {
        if (!scpData) return onError(isHolder);

        const {serial, type} = scpData;

        if (!type && !serial) return onError(isHolder);

        const state = getState();
        const deviceId = selectMwDeviceInternalId(state);

        if (isHolderOnly && !deviceId) return onError(isHolder);

        const getPreparedDeviceType = () => {
            const isInitialConnectOfOriginalDevice =
                initialDeviceType === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER &&
                (type === DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER || type === DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER);
            const isReconnectOfOriginalDevice =
                isReconnect &&
                (deviceType === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER ||
                    holderType === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER);

            if (isInitialConnectOfOriginalDevice || isReconnectOfOriginalDevice) {
                return isHolder
                    ? DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER
                    : DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER;
            }

            return type;
        };

        if (isHolder && serial) {
            const holderAsset = makeSelectAssetByType(isHolder)(state);
            const {serialNumber} = holderAsset || {};

            if (serialNumber && serialNumber !== serial) {
                //clear all data about previous holder if new one is connected
                pairingService.disconnectAndClearHolderState(true);
            }

            if (isHolderDetect && (!serialNumber || serialNumber !== serial)) {
                //holder reg popup should be shown if another holder is inserted or no holder was before
                dispatch(setIsSkipRegistration(false));
            }
        }

        const {codentify} = scpData || {};

        dispatch(
            updateIotDeviceData({
                [isHolder ? 'holder' : 'device']: {
                    deviceSerialNumber: serial,
                    codentify,
                    type: getPreparedDeviceType(),
                },
            })
        );

        if (isHolder) {
            const device = {isHolderConnected: true, holder_connected: true};
            const holderData = {holder_connected: true};

            dispatch(updateIotDeviceData({device, holder: holderData}));
        }

        return {serial, codentify, type};
    };

    const {serial: holderSerial, codentify: holderCodentify} = await initDeviceData(holder, true);
    const {serial, codentify, type} = isHolderOnly ? {} : await initDeviceData(charger);

    checkHolderConnectOnInit(isReconnect, isHolderOnly, isHolderDetect, holderSerial);

    const {codentify: chargerCodentify, newHolderCodentify} = await updateCodentifyAndAssets({
        serial,
        codentify,
        holderSerial,
        holderCodentify,
    });

    dispatch(setScpCheckInProgress(false));

    return {serial, codentify: chargerCodentify, holderSerial, holderCodentify: newHolderCodentify, type};
};

const checkHolderConnectOnInit = (isReconnect, isHolderOnly, isHolderDetect, holderSerial) => {
    if (!isReconnect && !isHolderOnly) {
        if (holderSerial) {
            dispatch(setIsHolderDetectedOnInit(true));
        } else {
            dispatch(setIsHolderDetectedOnInit(false));
        }
    } else if (isHolderDetect || (isReconnect && holderSerial)) {
        dispatch(setIsHolderDetectedOnInit(true));
    } else if (isReconnect) {
        dispatch(setIsHolderDetectedOnInit(false));
    }
};

const updateCodentifyAndAssets = async ({serial, codentify, holderSerial, holderCodentify, isForceCall}) => {
    const isHolderExists = !!holderSerial;
    const isCodentifyKnown =
        (codentify && holderCodentify) || (codentify && !isHolderExists) || (holderCodentify && !serial);

    if (isCodentifyKnown) {
        if (!appConfig.getIsRestrictedMarket()) {
            yapStateService.getProductInfoAssets(isHolderExists); // request to get assets is asynchronous
        }
        return {codentify, holderCodentify};
    }

    await yapStateService.getProductInfoAssets(isHolderExists, isForceCall);
    const codentifyID = updateDeviceCodentify(serial);
    const newHolderCodentify = updateDeviceCodentify(holderSerial, true);

    return {codentifyID, newHolderCodentify};
};

const updateDeviceCodentify = (serial, isHolder = false) => {
    const {codentifyID} = makeSelectConsumerInfoProduct(serial)(getState()) || {};

    if (!codentifyID) return;

    dispatch(
        updateIotDeviceData({
            [isHolder ? 'holder' : 'device']: {
                codentify: codentifyID,
            },
        })
    );

    return codentifyID;
};

const syncAndSubscribeOnDevice = async (isHolder) => {
    const isHolderConnected = selectIsHolderConnected(getState());

    if (isHolder && !isHolderConnected) {
        log.info('yapConnectionService: syncAndSubscribeOnDevice is ignored for holder. Holder is not connected');
        return;
    }

    const isOfflineMode = makeSelectIsOfflineYapFirmwareInProgress()(getState());
    const isDeviceSyncInProgress = makeSelectIsDeviceSyncInProgress()(getState());

    if (isOfflineMode) {
        dispatch(setOfflineYapFirmwareInProgress(false));
        cmClientService.disconnectCmClient(false);
        cmClientService.disconnectCmClient(true);
    }

    if (isHolder && !isDeviceSyncInProgress) {
        //reconnect charger if holder connect
        await syncAndSubscribeOnDevice();
    }

    await yapWebSocketService.initWebSocket(isHolder);

    await cmClientService.initCmClient(isHolder);
};

const initSynchronizeFlow = (isHolder) => {
    const state = getState();
    const {device, holder} = makeSelectIotDeviceMergedWithConsumerProduct()(state);

    if (!device || (isHolder && !holder)) return false;

    if (!pairingService.isDeviceConnected()) return false;

    //don't use await to run parallel flows for charger and holder
    synchronizeDevice(isHolder);
    return true;
};

const synchronizeDevice = async (isHolder, isRefresh) => {
    const isGamBackend = backendService.isGamBackend();
    const isDeviceSyncInProgress = makeSelectIsDeviceSyncInProgress(isHolder)(getState());
    const profilerProcessType = isHolder
        ? profilerProcessTypes.CONNECT_AND_SYNC_HOLDER
        : profilerProcessTypes.CONNECT_AND_SYNC;

    if (isDeviceSyncInProgress) return;

    if (isRefresh) {
        log.info(`Start refresh process. isHolder: ${!!isHolder}`);
    } else {
        profiler.start(profilerProcessType);

        log.info(`Start connect process. isHolder: ${!!isHolder}`);
    }

    yapFlowTimeoutService.clearFlowTimeout(yapFlowTypes.SYNCHRONIZE, isHolder);
    dispatch(setYapSyncInProgress(true, isHolder));

    const onError = () => {
        dispatch(setYapSyncInProgress(false, isHolder));
    };

    const isCancelSuccess = await yapClientService.waitUntilDataCollectionCanceled();

    if (!isCancelSuccess) {
        return onError();
    }

    try {
        const assetId = await yapService.getSyncAsset(isHolder);

        if (!assetId) return onError();

        const isDeviceSupported = marketConfigService.isDeviceSupportedForceCheck(isHolder);

        if (!isDeviceSupported) {
            dispatch(setIsDeviceNotSupported());
            return onError();
        }

        const isDeviceNotSupported = selectIsDeviceNotSupported(getState());

        if (isDeviceNotSupported) {
            return onError();
        }

        if (!isRefresh) {
            if (isHolder) {
                //due holder possible sbl mode reconnect
                dispatch(setYapHolderConnected(false));
            } else {
                //due device ping (holder state and veev 100% battery state)
                dispatch(setYapDeviceConnected(false));
            }
        }

        await syncAndSubscribeOnDevice(isHolder);
        const isConnected = await cmService.waitUntilConnected({isHolder});

        if (!isConnected) return onError();

        const {isHolderSbl, isSblMode} = iotDeviceService.checkIfDeviceIsInSblModeStatic();

        if (isSblMode) {
            const asset = makeSelectAssetByType(isHolder)(getState());

            if (asset) {
                //update image if device in sbl
                yapStateService.updateAssetMedia(asset);
            }

            if (isHolder && isHolderSbl) {
                dispatch(setHolderInfoReceived(true));
                dispatch(setYapSyncInProgress(false, isHolder));
                return;
            } else if (!isHolder && !isHolderSbl) {
                dispatch(setDeviceInfoReceived(true));
                dispatch(setYapSyncInProgress(false, isHolder));
                return;
            }
        }

        if (!pairingService.isDeviceConnected()) return onError();

        log.info(`Start synchronization process. isHolder: ${!!isHolder}`);

        const {requestId} =
            (isGamBackend
                ? await new GamClient().callPost({
                      methodName: gamMethods.SYNCHRONIZE_DATA,
                      requestConfig: {args: assetId},
                      isNeedRedirectForOtherErrors: false,
                  })
                : await new UamClient().callPost({
                      methodName: uamMethods.SYNCHRONIZE_DEVICE,
                      requestConfig: {args: assetId},
                      isNeedRedirectForOtherErrors: false,
                  })) || {};

        if (!requestId) {
            const profilerState = profiler.stop(profilerProcessType);

            log.info(`End synchronization process. Request id is not defined. isHolder: ${isHolder}`, {
                ...profilerState,
            });
            return onError();
        }

        dispatch(setSyncLastRequestId(requestId, isHolder));

        yapFlowTimeoutService.createFlowTimeout({
            type: yapFlowTypes.SYNCHRONIZE,
            timeoutInSec: isGamBackend ? gamTimeouts.GAM_SYNC_TIMEOUT : uamTimeouts.UAM_SYNC_TIMEOUT,
            isHolder,
        });
    } catch (e) {
        const profilerState = profiler.stop(profilerProcessType);

        log.info(`synchronizeDevice failed. isHolder: ${isHolder} error: ${e}`, {...profilerState});
        onError();
    }
};

const proceedRefreshInfo = async (status, assetId) => {
    const isHolder = makeSelectIsHolderByAssetId(assetId)(getState());
    let isSuccess = false;
    const profilerProcessType = isHolder
        ? profilerProcessTypes.CONNECT_AND_SYNC_HOLDER
        : profilerProcessTypes.CONNECT_AND_SYNC;

    const onReturn = () => {
        const profilerState = profiler.stop(profilerProcessType);

        if (isSuccess) {
            log.info(`End synchronization process. Success. isHolder: ${isHolder}`, {...profilerState});
        } else {
            log.info(`End synchronization process. Failed. isHolder: ${isHolder}`, {...profilerState});
        }

        dispatch(setYapSyncInProgress(false, isHolder));
    };

    if (yapActionStatusTypes.SUCCESS === status) {
        let isDeviceConnected = pairingService.isDeviceConnected();

        if (!isDeviceConnected) return onReturn();

        if (isHolder) {
            const isHolderConnected = getIsHolderConnected();

            if (!isHolderConnected) return onReturn();
        }

        const isGamBackend = backendService.isGamBackend();
        const isDeviceInfoValid = isGamBackend
            ? await gamClientService.getGamDeviceInfo(assetId)
            : await uamClientService.getUamInfo(assetId);

        if (!isDeviceInfoValid) return onReturn();

        isDeviceConnected = pairingService.isDeviceConnected();
        if (!isDeviceConnected) return onReturn();

        if (isHolder) {
            const isHolderConnected = getIsHolderConnected();

            if (!isHolderConnected) return onReturn();
        }

        isSuccess = true;
        if (isHolder) {
            dispatch(setHolderInfoReceived(true));
        } else {
            dispatch(setDeviceInfoReceived(true));
        }
    }

    onReturn();
};

const getIsHolderConnected = () => {
    const state = getState();
    const isHolderConnected = selectIsHolderConnected(state);

    return isHolderConnected;
};

const proceedConnectionChange = (status, assetId, isSBLMode, engine) => {
    const state = getState();
    const isHolder = makeSelectIsHolderByAssetId(assetId)(state);

    if (status === yapConnectionStatusTypes.CONNECTED) {
        const isHolderConnected = getIsHolderConnected();

        if (isHolder && !isHolderConnected) return;

        const sblMode = {is_device_in_sbl_mode: isSBLMode};
        const data = isHolder ? {holder: sblMode} : {device: sblMode};

        dispatch(updateIotDeviceData(data));

        if (connectivityService.isUsb()) {
            yapStateService.updateEngineData(isHolder, engine);
        }

        if (cmClientService.isCmClientConnected(isHolder)) {
            log.info(`uamClientService: connection of the device is changed. CONNECTED for isHolder ${isHolder}`);
            if (isHolder) {
                dispatch(setYapHolderConnected(true));
            } else {
                dispatch(setYapDeviceConnected(true));
            }
        }
    } else if (status === yapConnectionStatusTypes.DISCONNECTED) {
        log.info(`uamClientService: connection of the device is changed. DISCONNECTED for isHolder ${isHolder}`);
        if (isHolder) {
            dispatch(setYapHolderConnected(false));
        } else {
            dispatch(setYapDeviceConnected(false));
        }
    } else if (status === yapConnectionStatusTypes.INCORRECT_DEVICE) {
        log.info(`End synchronization process. Incorrect Device. isHolder: ${isHolder}`);
        dispatch(setIsWrongDevice());
        dispatch(setYapSyncInProgress(false, isHolder));
    }
};

const checkConnectivityStatus = async (isHolder) => {
    const assetId = makeSelectAssetIdByType(isHolder)(getState());
    const isGamBackend = backendService.isGamBackend();
    const isConnected = await cmClientService.isCmClientConnected(isHolder);

    if (!isConnected) return;

    const data = isGamBackend
        ? await new GamClient().callGet({
              methodName: gamMethods.CHECK_CONNECTIVITY_STATUS,
              requestConfig: {args: assetId},
              isNeedRedirectForOtherErrors: false,
          })
        : await new UamClient().callGet({
              methodName: uamMethods.CHECK_CONNECTIVITY_STATUS,
              requestConfig: {args: assetId},
              isNeedRedirectForOtherErrors: false,
          });

    if (!data) return;

    const {status, isSBLMode} = data;

    proceedConnectionChange(status, assetId, isSBLMode);
};

const isDeviceAlreadyConnected = async () => {
    try {
        const isGamBackend = backendService.isGamBackend();
        const assetId = await yapService.getSyncAsset();

        const data = isGamBackend
            ? await new GamClient().callGet({
                  methodName: gamMethods.CHECK_CONNECTIVITY_STATUS,
                  requestConfig: {args: assetId},
                  isNeedRedirectForOtherErrors: false,
              })
            : await new UamClient().callGet({
                  methodName: uamMethods.CHECK_CONNECTIVITY_STATUS,
                  requestConfig: {args: assetId},
                  isNeedRedirectForOtherErrors: false,
              });

        const {status} = data || {};

        return status === yapConnectionStatusTypes.CONNECTED || status === yapConnectionStatusTypes.WAITING_CONNECTION;
    } catch (e) {
        return false;
    }
};

const forceReconnectToCm = async (isHolder, isForceDisconnect = true) => {
    if (isForceDisconnect) {
        cmClientService.disconnectCmClient(isHolder);
    }

    const state = getState();
    const isNeedReconnect = isHolder
        ? makeSelectIsYapHolderInfoReceived()(state)
        : makeSelectIsYapDeviceInfoReceived()(state);

    if (isNeedReconnect) {
        await syncAndSubscribeOnDevice(isHolder);
    }
};

export default {
    checkConnectivityStatus,
    forceReconnectToCm,
    isDeviceAlreadyConnected,
    initScpData,
    initSynchronizeFlow,
    synchronizeDevice, //used for tests. use initSynchronizeFlow instead
    proceedConnectionChange,
    proceedRefreshInfo,
    syncAndSubscribeOnDevice,
    updateCodentifyAndAssets,
};
